import { useCallback, useEffect, useRef, useState } from 'react';

export type UseResizablePanelOptions = {
  storageKey: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  /** Which side the sized panel sits on. Default `left`. */
  side?: 'left' | 'right';
};

export function useResizablePanel({
  storageKey,
  defaultWidth = 240,
  minWidth = 180,
  maxWidth = 420,
  side = 'left',
}: UseResizablePanelOptions) {
  const loadWidth = () => {
    try {
      const raw = localStorage.getItem(`${storageKey}:width`);
      const n = raw ? Number(raw) : NaN;
      if (Number.isFinite(n) && n >= minWidth && n <= maxWidth) return n;
    } catch {
      /* ignore */
    }
    return defaultWidth;
  };

  const loadCollapsed = () => {
    try {
      return localStorage.getItem(`${storageKey}:collapsed`) === '1';
    } catch {
      return false;
    }
  };

  const [panelWidth, setPanelWidth] = useState(loadWidth);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [panelResizing, setPanelResizing] = useState(false);
  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const splitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}:width`, String(panelWidth));
    } catch {
      /* ignore */
    }
  }, [storageKey, panelWidth]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}:collapsed`, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [storageKey, collapsed]);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || collapsed) return;
      e.preventDefault();
      resizingRef.current = true;
      setPanelResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent) => {
        if (!resizingRef.current || !splitRef.current) return;
        const rect = splitRef.current.getBoundingClientRect();
        const raw =
          side === 'right' ? rect.right - ev.clientX : ev.clientX - rect.left;
        const next = Math.min(maxWidth, Math.max(minWidth, raw));
        panelWidthRef.current = next;
        setPanelWidth(next);
      };

      const onUp = () => {
        resizingRef.current = false;
        setPanelResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [collapsed, maxWidth, minWidth, side],
  );

  return {
    splitRef,
    panelWidth,
    collapsed,
    setCollapsed,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  };
}
